import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSidebar } from "../../contexts/SidebarContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAgents } from "./hooks/useAgents";
import { WORLDS } from "./config/worlds";
import { AGENT_LAYOUT } from "./config/layout";
import KingdomMap from "./components/KingdomMap";
import WorldMap from "./components/WorldMap";
import FocusPanel from "./components/FocusPanel";

/**
 * The Agents page — two-level navigation:
 *   overview (kingdom) → world map (houses, couriers) → agent sheet
 *
 * Fills the viewport to the right of the CRM sidebar (everything inside
 * positions absolutely against this container, so the sidebar stays usable).
 *
 * Live: agent state comes from Firestore `agents`, written by Grok Bot through
 * the CRM MCP server (report_agent_status / log_agent_event). Buttons write
 * `agent_commands` that Grok picks up with get_agent_commands.
 */
export default function AgentKingdom({ db }) {
  const { currentUser } = useAuth();
  const { agents, byWorld, needsYou, working, reporting, api, error } = useAgents({ db, user: currentUser });
  const { isCollapsed } = useSidebar();

  // view: { mode: "overview" } | { mode: "world", world }
  const [view, setView] = useState({ mode: "overview" });
  const [panel, setPanel] = useState(null); // null | {mode:"king"} | {mode:"agent", id}
  const [panelOpen, setPanelOpen] = useState(false);

  const selectedAgent = useMemo(
    () => (panel?.mode === "agent" ? agents.find((a) => a.id === panel.id) : null),
    [agents, panel]
  );

  const openPanel = useCallback((p) => {
    setPanel(p);
    setPanelOpen(false);
    setTimeout(() => setPanelOpen(true), 60);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setTimeout(() => setPanel(null), 300);
  }, []);

  const enterWorld = useCallback(
    (world, agentId = null) => {
      closePanel();
      setView({ mode: "world", world, entering: true });
      if (agentId) setTimeout(() => openPanel({ mode: "agent", id: agentId }), 700);
    },
    [closePanel, openPanel]
  );

  const goOverview = useCallback(() => {
    closePanel();
    setView({ mode: "overview" });
  }, [closePanel]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (panel) closePanel();
      else if (view.mode === "world") goOverview();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel, view.mode, closePanel, goOverview]);

  const inWorld = view.mode === "world";
  const panelSide =
    panel?.mode === "agent" && selectedAgent
      ? (AGENT_LAYOUT[selectedAgent.world]?.[selectedAgent.id]?.x ?? 0) > 52
        ? "left"
        : "right"
      : "right";

  return (
    <div
      className="fixed inset-y-0 right-0 overflow-hidden bg-gradient-to-b from-sky-500 via-sky-300 to-sky-200 font-body text-white"
      style={{ left: isCollapsed ? 80 : 256, transition: "left 0.3s ease" }}
    >
      {/* layers */}
      <div className={`transition-opacity duration-500 ${inWorld ? "pointer-events-none opacity-0" : "opacity-100"}`}>
        <KingdomMap
          focus={null}
          onFocus={(k) => (k === "king" ? openPanel({ mode: "king" }) : enterWorld(k))}
          byWorld={byWorld}
          needsYou={needsYou}
        />
      </div>
      {/* absolute inset-0 matters: the world-in animation leaves a transform on
          the wrapper, making it the containing block for WorldMap's inset-0 —
          unsized, that clips the whole world to 0px */}
      {inWorld && (
        <div className="animate-world-in absolute inset-0">
          <WorldMap
            world={view.world}
            db={db}
            agents={agents}
            selectedId={panel?.mode === "agent" ? panel.id : null}
            onSelectAgent={(id) => openPanel({ mode: "agent", id })}
          />
        </div>
      )}

      {/* HUD */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-40 flex items-center gap-3.5 p-4">
        {/* world title, centered so it clears the Back to the Kingdom button */}
        {inWorld && (
          <div className="pointer-events-auto absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2.5 rounded-2xl border-2 border-white/20 bg-[#122A4A]/90 px-4 py-2 shadow-lg">
            <span className="text-xl">👑</span>
            <div>
              <h1 className="font-display text-[17px] font-semibold tracking-wide">{WORLDS[view.world].name}</h1>
              <span className="text-[11px] font-bold uppercase tracking-widest text-sky-300/60">{WORLDS[view.world].sub}</span>
            </div>
          </div>
        )}
        <div className="pointer-events-auto ml-auto flex gap-2.5">
          <div className="flex items-center gap-2 rounded-xl border-2 border-white/20 bg-[#122A4A]/90 px-3.5 py-2 font-display text-sm font-semibold shadow-lg" title="Agents that have reported through the MCP">
            <span className="text-sky-300/70">{reporting}/{agents.length}</span> reporting
          </div>
          <div className="flex items-center gap-2 rounded-xl border-2 border-white/20 bg-[#122A4A]/90 px-3.5 py-2 font-display text-sm font-semibold shadow-lg">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px] shadow-emerald-400" />
            {working} working
          </div>
          <div className={`flex items-center gap-2 rounded-xl border-2 bg-[#122A4A]/90 px-3.5 py-2 font-display text-sm font-semibold shadow-lg ${needsYou.length ? "animate-pulse border-amber-300/60" : "border-white/20"}`}>
            Needs you
            <span className="grid h-5 min-w-[22px] place-items-center rounded-full bg-amber-400 px-1.5 text-[13px] font-bold text-amber-950">
              {needsYou.length}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="absolute left-1/2 top-20 z-40 -translate-x-1/2 rounded-xl border-2 border-rose-400/70 bg-[#122A4A]/95 px-4 py-2 font-display text-sm font-semibold text-rose-200 shadow-lg">
          Couldn't load live agent data — {error}
        </div>
      )}

      {/* back button */}
      {inWorld && (
        <button
          type="button"
          onClick={goOverview}
          className="absolute left-4 top-4 z-40 flex items-center gap-2 rounded-xl border-2 border-white/20 bg-[#122A4A]/90 px-4 py-2 font-display text-sm font-semibold shadow-lg transition hover:scale-105 hover:border-amber-400"
        >
          ← Back to the Kingdom
        </button>
      )}

      {/* hint */}
      <div className={`absolute bottom-4 left-1/2 z-40 -translate-x-1/2 whitespace-nowrap rounded-full border-2 border-white/20 bg-[#122A4A]/90 px-5 py-2 font-display text-[13.5px] text-sky-200/90 shadow-lg transition-opacity duration-500 ${panel ? "pointer-events-none opacity-0" : ""}`}>
        {inWorld ? (
          <>Click a <b className="font-semibold text-amber-400">house</b> to see that agent's work</>
        ) : (
          <>Click <b className="font-semibold text-amber-400">the King</b> for the royal ledger, or step into a <b className="font-semibold text-amber-400">world</b></>
        )}
      </div>

      <FocusPanel
        db={db}
        mode={panel?.mode}
        open={Boolean(panel) && panelOpen}
        side={panelSide}
        agents={agents}
        agent={selectedAgent}
        api={api}
        onClose={closePanel}
        onJumpWorld={(k) => enterWorld(k)}
        onJumpAgent={(k, id) => (inWorld && view.world === k ? openPanel({ mode: "agent", id }) : enterWorld(k, id))}
      />
    </div>
  );
}
